<template>
  <div
    :id="componentId"
    :class="[
      'vc-select',
      {
        'vc-select--readonly': readonly,
        'vc-select--disabled': disabled,
        'vc-select--error': error,
        'vc-select--autocomplete': autocomplete,
        'vc-select--opened': isShown,
      },
    ]"
  >
    <VcLabel v-if="label" :required="required" :error="error">
      {{ label }}
    </VcLabel>

    <VcDropdownMenu
      class="vc-select__container"
      width="100%"
      :disabled="!enabled"
      disable-trigger-events
      @toggle="toggled"
    >
      <template #trigger="{ open, close, toggle }">
        <!-- Custom SVG Icon Slot -->
        <span v-if="$slots.prepend || prependIcon" class="vc-select__prepend">
          <slot name="prepend">
            <VcIcon v-if="prependIcon" class="vc-select__icon" :name="prependIcon" />
          </slot>
        </span>
        <div
          v-if="$slots.selected || $slots.placeholder"
          tabindex="0"
          role="button"
          class="vc-select__button"
          @click="toggle"
          @keydown.enter="toggle"
          @keydown.down.prevent="next(-1)"
        >
          <div class="vc-select__button-content">
            <slot v-if="selected" name="selected" v-bind="{ item: selected as TSelectItemType, error }" />
            <slot v-else-if="!selected && $slots.placeholder" name="placeholder" v-bind="{ error }" />
          </div>

          <VcIcon
            class="vc-select__icon shrink-0 stroke-[white] stroke-2"
            :name="isShown ? 'chevron-up' : 'chevron-down'"
            size="xs"
          />
        </div>

        <VcInput
          v-else
          v-model="search"
          class="vc-select__input"
          :name="name"
          :required="required"
          :size="size"
          :placeholder="placeholderText"
          :disabled="disabled"
          :readonly="readonly || !autocomplete"
          :error="error"
          truncate
          @keydown.down.prevent="next(-1)"
          @focus="open"
          @click="(autocomplete && open) || (!autocomplete && toggle)"
        >
          <template #append>
            <button
              v-if="clearable && (filterValue || (selectedText && !isShown))"
              :aria-label="$t('common.buttons.toggle_dropdown')"
              type="button"
              tabindex="-1"
              class="vc-select__clear"
              @click="clear"
            >
              <VcIcon name="delete-mini" :size="16" />
            </button>

            <button
              :aria-label="$t('common.buttons.toggle_dropdown')"
              type="button"
              tabindex="-1"
              class="vc-select__arrow"
              @click="handleArrowClick($event, close)"
            >
              <VcIcon
                :name="isShown ? 'chevron-up' : 'chevron-down'"
                size="xs"
                class="shrink-0 stroke-[white] stroke-2"
              />
            </button>
          </template>
        </VcInput>
      </template>

      <template v-if="enabled" #content="{ close }">
        <VcMenuItem
          v-for="(item, index) in filteredItems"
          :key="index"
          :active="isActiveItem(item)"
          :aria-selected="isActiveItem(item)"
          role="option"
          size="md"
          @click="
            select(item);
            !multiple && close();
          "
          @keyup.esc="close()"
          @keydown.up.prevent="prev(index)"
          @keydown.down.prevent="next(index)"
        >
          <VcCheckbox v-if="multiple" :model-value="isActiveItem(item)" tabindex="-1" />
          <slot name="item" v-bind="{ item, index }">
            {{ getItemText(item) }}
          </slot>
        </VcMenuItem>

        <VcMenuItem v-if="filterValue && !filteredItems.length" disabled>
          {{ $t("common.messages.no_results") }}
        </VcMenuItem>
      </template>
    </VcDropdownMenu>

    <VcInputDetails :show-empty="showEmptyDetails" :message="message" :error="error" :single-line="singleLineMessage" />
  </div>
</template>

<script setup lang="ts" generic="TSelectItemType extends SelectItemType">
/* eslint-disable prettier/prettier */
/* eslint-disable curly */
/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: https://virtocommerce.atlassian.net/browse/ST-5117
import { union, lowerCase, isEqual } from "lodash";
import { computed, ref, unref  } from "vue";
import { useI18n } from "vue-i18n";
import { useComponentId } from "@/ui-kit/composables";
import type { SelectItemType } from "./types";
import type {ComputedRef} from "vue";

export interface IProps {
  //It appears generics do not work well with compiler macros, so we use unknown
  //modelValue?: TSelectItemType | Array<TSelectItemType> | Ref<TSelectItemType | Array<TSelectItemType>>;
  modelValue?: unknown;
  name?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  //items: (Record<string, unknown> | string)[] | Ref<(Record<string, unknown> | string)[]>;
  items: unknown;
  size?: "sm" | "md" | "auto";
  itemSize?: "sm" | "md" | "lg";
  textField?: string;
  valueField?: string;
  placeholder?: string;
  showEmptyDetails?: boolean;
  error?: boolean;
  message?: string;
  autocomplete?: boolean;
  singleLineMessage?: boolean;
  multiple?: boolean;
  clearable?: boolean;
  prependIcon?: string;
  appendIcon?: string;
  icon?: boolean | string;
  staticSelectionText?: string; //If this property is set, it will override '1 item selected' or '2 items selected' with provided text
}

export interface IEmits {
  (
    event: "update:modelValue",
    //value?: string | Record<string, unknown> | Array<Record<string, unknown> | string>,
    value?: any //Generics can't be used here so we have to resort to "any"
  ): void;
  (event: "change", value?: any): void;
}

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  size: "md",
  itemSize: "sm",
});

const { t } = useI18n();
const componentId = useComponentId("select");

const isShown = ref(false);
const filterValue = ref("");

const getItemText = (item: TSelectItemType) => {
  if (item instanceof String) {
    return item as string;
  } else if (item instanceof Object && props.textField) {
    return item[props.textField] as string;
  }

  return ""; //This should never happen
};
const getItemKeyValue = (item: TSelectItemType) => {
  if (item instanceof String) {
    return item as string;
  } else if (item instanceof Object && props.valueField) {
    return item[props.valueField] as string;
  }

  return ""; //This should never happen
};

const itemsFromProps : ComputedRef<TSelectItemType[]> = computed(() => 
{
  const rawItems = unref(props.items);

  if (Array.isArray(rawItems))
    return rawItems as Array<TSelectItemType>;

  return [];
});

const modelKeyValueFromProps : ComputedRef<string | string[]> = computed(() => 
{
  const rawModelValue = unref(props.modelValue);

  if (Array.isArray(rawModelValue))
    return rawModelValue as Array<string>;

  return rawModelValue as string;
});

const selectedText = computed(() => {

  if (props.staticSelectionText) {
    return props.staticSelectionText;
  }

  if (Array.isArray(modelKeyValueFromProps.value)) {
    if (modelKeyValueFromProps.value.length) {
      if (modelKeyValueFromProps.value.length === 1) {
        return t("ui_kit.select.item_selected");
      } else {
        return t("ui_kit.select.items_selected", [modelKeyValueFromProps.value.length]);
      }
    }

    return "";
  }

  if (props.textField && selected.value instanceof Object) {
    return (<Record<string, unknown>>selected.value)[props.textField] as string;
  }

  return selected.value as string;
});

const placeholderText = computed(() =>
  selectedText.value ? (selectedText.value !== "" ? selectedText.value : props.placeholder) : props.placeholder,
);

const enabled = computed<boolean>(() => !props.readonly && !props.disabled);

const selected = computed(() => {
  //Case for multi-value select
  if (Array.isArray(modelKeyValueFromProps.value)) {
    const modelKeyValueArray : string[] = modelKeyValueFromProps.value;
    return modelKeyValueArray.map((selectedValue) =>
      itemsFromProps.value.find((item) => getItemKeyValue(item) === selectedValue),
    );
  }

  //Case for single value select
  return itemsFromProps.value.find((item) => getItemKeyValue(item) === modelKeyValueFromProps.value);
});

const search = computed({
  get() {
    if (props.autocomplete && isShown.value) {
      return filterValue.value;
    }

    return selectedText.value;
  },
  set(value) {
    filterValue.value = value;
  },
});

const filteredItems = computed(() => {
  if (!filterValue.value) {
    return itemsFromProps.value;
  }

  const searching = lowerCase(filterValue.value);
  const items = itemsFromProps.value.filter((item) => lowerCase(getItemText(item)).includes(searching));

  const first = items.filter((item) => lowerCase(getItemText(item)).indexOf(searching) === 0);

  return union(first, items);
});

function isActiveItem(item: TSelectItemType) {
  const itemValue = getItemKeyValue(item);

  if (!Array.isArray(modelKeyValueFromProps.value)) {
    return itemValue === modelKeyValueFromProps.value;
  }

  return modelKeyValueFromProps.value.some((keyValue) => isEqual(keyValue, itemValue));
}

function select(item: TSelectItemType) {
  if (!enabled.value) {
    return;
  }

  if (props.multiple && Array.isArray(modelKeyValueFromProps.value)) {
    let newValues = [...modelKeyValueFromProps.value];

    const existingIndex = newValues.findIndex((existingKeyValue) =>
      isEqual(existingKeyValue, getItemKeyValue(item)),
    );

    if (existingIndex >= 0) {
      newValues = [...newValues.slice(0, existingIndex), ...newValues.slice(existingIndex + 1)];
    } else {
      newValues.push(getItemKeyValue(item));
    }

    emit("update:modelValue", newValues);
    emit("change", newValues);
  } else {
    const newValue = getItemKeyValue(item);

    if (newValue !== modelKeyValueFromProps.value) {
      emit("update:modelValue", newValue);
      emit("change", newValue);
    }
  }
}

function getItemsElements() {
  return Array.from(document.querySelectorAll(`#${componentId} [role='option'] [tabindex='0']`));
}

function next(index: number) {
  const elements = getItemsElements();

  if (elements?.length) {
    const focusItemIndex = index === elements?.length - 1 ? 0 : index + 1;
    const nextElement = elements[focusItemIndex];

    if (nextElement instanceof HTMLElement) {
      nextElement.focus();
    }
  }
}

function prev(index: number) {
  const elements = getItemsElements();

  if (elements?.length) {
    const focusItemIndex = index === 0 ? elements?.length - 1 : index - 1;
    const prevElement = elements[focusItemIndex];

    if (prevElement instanceof HTMLElement) {
      prevElement.focus();
    }
  }
}

function toggled(value: boolean) {
  isShown.value = value;

  if (!isShown.value) {
    filterValue.value = "";
  }
}

function clear() {
  if (
    ((!isShown.value && props.autocomplete) || !props.autocomplete) &&
    props.multiple &&
    Array.isArray(modelKeyValueFromProps.value) &&
    modelKeyValueFromProps.value.length
  ) {
    emit("update:modelValue", []);
  } else if (filterValue.value) {
    filterValue.value = "";
  } else if (!props.multiple && !isShown.value) {
    emit("update:modelValue", undefined);
  }
}

function handleArrowClick(event: MouseEvent, close: () => void) {
  if (isShown.value) {
    event.stopPropagation();
    close();
  }
}
</script>

<style lang="scss">
.vc-select {
  $disabled: "";
  $readonly: "";
  $autocomplete: "";
  $opened: "";
  $error: "";
  $prepend: "";
  $append: "";
  $icon: "";

  @apply flex flex-col gap-y-2;

  &--disabled {
    $disabled: &;
  }

  &--readonly {
    $readonly: &;
  }

  &--autocomplete {
    $autocomplete: &;
  }

  &--opened {
    $opened: &;
  }

  &--error {
    $error: &;
  }

  &__prepend {
    @apply flex items-center;
    $prepend: &;

    &:empty {
      @apply hidden;
    }
  }

  &__append {
    $append: &;

    &:empty {
      @apply hidden;
    }
  }

  &__icon {
    #{$prepend} & {
      @apply me-2;
    }

    #{$append} & {
      @apply ms-2;
    }
  }

  &__container {
    @apply relative;
  }

  &__button {
    @apply relative flex items-center w-full rounded-md border bg-additional-50 appearance-none text-left;

    #{$disabled} &,
    &:disabled {
      @apply bg-neutral cursor-not-allowed pointer-events-none;
    }

    #{$readonly} & {
      @apply pointer-events-none;
    }

    #{$error} & {
      @apply border-[--color-danger-450];
    }

    &--opened,
    &:focus {
      @apply ring ring-1 ring-[--color-neutral-a2];
    }
  }

  &__button-content {
    @apply grow overflow-y-hidden flex flex-col justify-center min-w-0 h-full;

    #{$error} & {
      @apply -inset-px border-[--color-danger-500];
    }
  }

  &__input {
    @apply w-full cursor-pointer;

    & input {
      @apply cursor-pointer;

      #{$opened} & {
        @apply cursor-auto;
      }
    }

    & .vc-input {
      &__decorator {
        @apply self-center;
      }
    }
  }

  &__clear {
    @apply flex items-center h-full px-1.5 text-primary;

    &:hover {
      @apply text-primary-600;
    }
  }

  &__arrow {
    @apply flex items-center h-full pe-3 ps-1.5 text-neutral-900;

    &:hover {
      @apply text-neutral;
    }

    #{$disabled} & {
      @apply text-neutral;
    }
  }

  &__icon {
    @apply shrink-0 mr-3 text-neutral-900;

    #{$disabled} & {
      @apply text-neutral-400;
    }

    #{$readonly} & {
      @apply hidden;
    }
  }

  &__dropdown {
    @apply z-10 overflow-hidden absolute mt-1 w-full bg-additional-50 rounded border border-[--color-neutral-a4] shadow-lg;
  }

  &__list {
    @apply overflow-auto max-h-60;
  }

  .vc-dropdown-menu {
    &__list {
      @apply rounded-t-none rounded-md border border-[color:var(--color-neutral-a4)] lg:-mt-1 divide-y divide-[--color-neutral-a4] #{!important};
      box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4) !important;

      & > *:first-child {
        @apply rounded-t;
      }

      & > *:last-child {
        @apply rounded-b;
      }
    }
  }
}
</style>
